import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { ArrowUpDown, ArrowUp, ArrowDown, Users, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SearchBar } from '@/components/ui/search-bar';
import { Badge } from '@/components/ui/badge';
import { pillVariants } from '@/components/ui/pill';
import { customerService, type CustomerFilter } from '@/services/api/customer.service';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import { CustomerFormDialog } from '@/components/customers/CustomerFormDialog';

const PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;

type SortDir = 'asc' | 'desc';

const FILTER_OPTIONS: CustomerFilter[] = ['all', 'with_bookings', 'with_completed_bookings', 'without_bookings', 'recent'];

export default function AdminCustomersPage() {
  const { t } = useTranslation(['customers', 'common']);
  const [, setLocation] = useLocation();

  const [isCreateOpen, setIsCreateOpen] = useState(false);

  useSetPageHeader(
    {
      title: t('customers:title'),
      sub: t('customers:subtitle'),
      actions: (
        <Button
          className="hidden md:inline-flex"
          data-testid="create-customer-btn"
          size="md"
          onClick={() => setIsCreateOpen(true)}
          iconLeft={<Plus className="h-4 w-4" />}
        >
          <span>{t('customers:createDialog.button')}</span>
        </Button>
      ),
    },
    [t],
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDir, setSortDir] = useState<SortDir | null>(null);
  const [filter, setFilter] = useState<CustomerFilter>('all');

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-customers', debouncedSearch, currentPage, sortDir, filter],
    queryFn: () =>
      customerService.list({
        search: debouncedSearch || undefined,
        page: currentPage,
        perPage: PER_PAGE,
        sortBy: sortDir ? 'last_visit_at' : undefined,
        sortDir: sortDir ?? undefined,
        filter,
      }),
  });

  const customers = data?.customers ?? [];
  const meta = data?.meta;

  const handleToggleSortLastVisit = () => {
    setSortDir((prev) => (prev === 'desc' ? 'asc' : prev === 'asc' ? null : 'desc'));
    setCurrentPage(1);
  };

  const SortIcon = useMemo(() => {
    if (sortDir === 'asc') return <ArrowUp className="h-3.5 w-3.5" />;
    if (sortDir === 'desc') return <ArrowDown className="h-3.5 w-3.5" />;
    return <ArrowUpDown className="h-3.5 w-3.5 text-soft" />;
  }, [sortDir]);

  const navigateToCustomer = (id: number | string) => {
    setLocation(`/admin/customers/${id}`);
  };

  const handleFilterChange = (next: CustomerFilter) => {
    setFilter(next);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Search + filters toolbar */}
      <div className="rounded-xl border border-line bg-surface p-4 space-y-4">
        <SearchBar
          containerClassName="max-w-md"
          placeholder={t('customers:search.placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label={t('customers:search.placeholder')}
        />

        <div
          role="group"
          aria-label={t('customers:filters.ariaLabel')}
          className="flex flex-wrap gap-2"
        >
          {FILTER_OPTIONS.map((option) => {
            const active = filter === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => handleFilterChange(option)}
                aria-pressed={active}
                className={`${pillVariants({ variant: active ? 'selected' : 'default' })} whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1`}
              >
                {t(`customers:filters.${option}`)}
              </button>
            );
          })}
        </div>
      </div>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2/60">
                  <th
                    scope="col"
                    className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap"
                  >
                    {t('customers:table.name')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden sm:table-cell"
                  >
                    {t('customers:table.email')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden md:table-cell"
                  >
                    {t('customers:table.phone')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right font-semibold text-soft whitespace-nowrap hidden lg:table-cell"
                  >
                    {t('customers:table.visits')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden lg:table-cell"
                    aria-sort={sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none'}
                  >
                    <button
                      type="button"
                      onClick={handleToggleSortLastVisit}
                      className="inline-flex items-center gap-1.5 hover:text-ink focus:outline-none focus:ring-2 focus:ring-brand-500 rounded"
                    >
                      {t('customers:table.lastVisit')}
                      {SortIcon}
                    </button>
                  </th>
                  <th scope="col" className="px-4 py-3 text-right">
                    <span className="sr-only">{t('customers:table.actions')}</span>
                  </th>
                </tr>
              </thead>

              {isLoading && (
                <tbody>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-line">
                      <td className="px-4 py-3">
                        <div className="h-4 w-32 bg-surface-2 rounded animate-pulse" />
                        <div className="h-3 w-40 bg-surface-2/60 rounded animate-pulse mt-1.5 sm:hidden" />
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <div className="h-4 w-48 bg-surface-2 rounded animate-pulse" />
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="h-4 w-28 bg-surface-2 rounded animate-pulse" />
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="h-4 w-8 bg-surface-2 rounded animate-pulse ml-auto" />
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="h-4 w-24 bg-surface-2 rounded animate-pulse" />
                      </td>
                      <td className="px-4 py-3" />
                    </tr>
                  ))}
                </tbody>
              )}

              {!isLoading && (isError || customers.length === 0) && (
                <tbody>
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <Users className="h-10 w-10 mx-auto mb-3 text-soft" />
                      <p className="text-soft font-medium">
                        {isError
                          ? t('customers:empty.loadError')
                          : debouncedSearch || filter !== 'all'
                          ? t('customers:empty.noResults')
                          : t('customers:empty.noCustomers')}
                      </p>
                      {!debouncedSearch && filter === 'all' && !isError && (
                        <p className="text-sm text-soft mt-1">
                          {t('customers:empty.hint')}
                        </p>
                      )}
                    </td>
                  </tr>
                </tbody>
              )}

              {!isLoading && !isError && customers.length > 0 && (
                <tbody>
                  {customers.map((customer) => {
                    const linkId = customer.publicId ?? customer.id;
                    return (
                      <tr
                        key={customer.id}
                        onClick={() => navigateToCustomer(linkId)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigateToCustomer(linkId);
                          }
                        }}
                        tabIndex={0}
                        role="link"
                        aria-label={t('customers:actions.viewAriaLabel', { name: customer.fullName })}
                        className="border-b border-line last:border-0 hover:bg-surface-2 cursor-pointer transition-colors focus:outline-none focus:bg-surface-2"
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-ink">
                            {customer.fullName}
                          </p>
                          <p className="text-xs text-soft sm:hidden">
                            {customer.email}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-ink hidden sm:table-cell">
                          {customer.email}
                        </td>
                        <td className="px-4 py-3 text-ink hidden md:table-cell">
                          {customer.phone || (
                            <span className="text-soft">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right hidden lg:table-cell">
                          <Badge tone={customer.totalVisits > 0 ? 'info' : 'neutral'}>
                            {customer.totalVisits}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-ink hidden lg:table-cell whitespace-nowrap">
                          {customer.lastVisitAt ? (
                            format(parseISO(customer.lastVisitAt), getDateFormats().medium, {
                              locale: getDateLocale(),
                            })
                          ) : (
                            <span className="text-soft">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <ChevronRight className="h-4 w-4 inline-block text-soft" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>

          {meta && meta.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-4 border-t border-line">
              <div className="text-sm text-soft">
                {t('customers:pagination.showing', {
                  start: (meta.currentPage - 1) * PER_PAGE + 1,
                  end: Math.min(meta.currentPage * PER_PAGE, meta.totalCount),
                  total: meta.totalCount,
                })}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={meta.currentPage === 1}
                  aria-label={t('common:pagination.previous')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, meta.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (meta.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (meta.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (meta.currentPage >= meta.totalPages - 2) {
                      pageNum = meta.totalPages - 4 + i;
                    } else {
                      pageNum = meta.currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={meta.currentPage === pageNum ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="min-w-[44px]"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(meta.totalPages, p + 1))}
                  disabled={meta.currentPage === meta.totalPages}
                  aria-label={t('common:pagination.next')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CustomerFormDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSaved={(customer) => navigateToCustomer(customer.publicId ?? customer.id)}
      />
    </div>
  );
}
